import * as React from 'react'
import Dropzone from "dropzone"
import { useEffect, useRef } from 'react'
// Type definitions from dropzone module
type DropzoneFile = any

import { ProfileFormContext } from '../ProfileEditForm'
import { ACTIONS } from './ProfileFormReducer'
import { validateFilename, getFilenameValidationMessage } from '../../../../shared/utils/filenameValidation'

// Prevent Dropzone from auto-discovering forms
Dropzone.autoDiscover = false

// Submit ProfileResumeDeleteField
const submitProfileResumeDeleteField = (profile: any, dispatch: React.Dispatch<any>, authenticity_token: string, setFiles: React.Dispatch<React.SetStateAction<any[]>>) => {
  const profileData = {
    delete_resume: '1',
    profile: {
      delete_resume: '1',
    }
  }

  const url = `/profiles/${profile.id}.json`
  const method = "PATCH"
  const headers = {
    "X-CSRF-Token": authenticity_token,
    "Content-Type": "application/json",
  }

  fetch(url, {
    method: method,
    headers: headers,
    body: JSON.stringify(profileData),
  })
    .then((response) => {
      if (response.ok) {
        return response.json()
      }
      // throw new Error("Network response was not ok.")
    })
    .then((_response) => {
      // Dispatch SET_RESUME to null
      dispatch({
        type: ACTIONS.SET_RESUME,
        payload: {
          resume: null,
          resume_file_name: null
        }
      })
      setFiles([])
    })
    .catch((error) => console.error("Error:", error))
}


export default function ResumeUpload() {
  const dropzoneRef = useRef<HTMLDivElement>(null);
  const myDropzone = useRef<Dropzone | null>(null);

  // Context
  const [state, dispatch] = React.useContext(ProfileFormContext)

  const [files, setFiles] = React.useState(
    state.profile?.resume_file_name ? [{ name: state.profile.resume_file_name }] : []
  )

  // Filename validation state
  const [filenameWarning, setFilenameWarning] = React.useState('')

  // Upload state management
  const [uploadError, setUploadError] = React.useState('')
  const [uploadSuccess, setUploadSuccess] = React.useState(false)
  const [isUploading, setIsUploading] = React.useState(false)

  // Resume parsing state
  const [resumeParseId, setResumeParseId] = React.useState<number | null>(null)
  const [isParsing, setIsParsing] = React.useState(false)

  useEffect(() => {
    if (!myDropzone.current) {
      myDropzone.current = new Dropzone(dropzoneRef.current, {
        url: "/profiles/" + state.profile.id + "/upload_resume",
        paramName: "file", // Explicitly set the parameter name
      });

      // Options.iploadProfileDocuments headers
      myDropzone.current.options.headers = {
        "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''
      }

      // File size and type configuration
      myDropzone.current.options.maxFilesize = 10; // 10MB max file size
      myDropzone.current.options.acceptedFiles = '.pdf,.doc,.docx,.jpg,.jpeg,.png,.gif';

      // dictDefaultMessage
      myDropzone.current.options.dictDefaultMessage = "Drag and drop your resume here or click to upload"

      // addRemoveLinks
      myDropzone.current.options.addRemoveLinks = false

      // dictRemoveFileConfirmation
      myDropzone.current.options.dictRemoveFileConfirmation = "Are you sure you want to remove this file?"

      // File size error message
      myDropzone.current.options.dictFileTooBig = "File is too large ({{filesize}}MB). Maximum file size: {{maxFilesize}}MB."

      // Invalid file type message
      myDropzone.current.options.dictInvalidFileType = "You can't upload files of this type. Accepted formats: PDF, DOC, DOCX, JPG, PNG, GIF"

      // createImageThumbnails
      myDropzone.current.options.createImageThumbnails = false as any

      // Preview Template with image
      (myDropzone.current.options as any).previewTemplate = `
        <div class="dz-preview dz-file-preview mb-3">
          <div class="card shadow-sm">
            <div class="card-body p-3">
              <div class="row align-items-center">
                <div class="col-auto">
                  <div class="bg-light rounded p-2">
                    <i class="fas fa-file-alt text-primary" style="font-size: 2rem;"></i>
                  </div>
                </div>
                <div class="col">
                  <div class="dz-filename text-truncate fw-semibold"><span data-dz-name></span></div>
                  <div class="dz-size text-muted small" data-dz-size></div>
                  <div class="dz-progress progress mt-2" style="height: 4px;">
                    <div class="dz-upload progress-bar bg-primary" role="progressbar" data-dz-uploadprogress style="transition: width 0.3s ease;"></div>
                  </div>
                </div>
                <div class="col-auto">
                  <div class="dz-remove">
                    <button class="btn btn-outline-danger btn-sm" data-dz-remove type="button" title="Remove file">
                      <i class="fas fa-times"></i> Remove
                    </button>
                  </div>
                </div>
              </div>
              <div class="dz-error-message text-danger small mt-2"><span data-dz-errormessage></span></div>
            </div>
          </div>
        </div>
        `

      myDropzone.current.on("addedfile", (file: DropzoneFile) => {
        setUploadError('')
        setUploadSuccess(false)

        // Remove any existing files from dropzone first (only one resume allowed)
        myDropzone.current.files.forEach((existingFile: DropzoneFile) => {
          if (existingFile !== file) {
            myDropzone.current.removeFile(existingFile);
          }
        });

        // Validate filename and show warnings
        if (file.name) {
          const validationResult = validateFilename(file.name);
          if (validationResult.hasProblematicCharacters) {
            const message = getFilenameValidationMessage(validationResult);
            setFilenameWarning(message);
          } else {
            setFilenameWarning('');
          }
        }

        // Replace the files array with just the new file (only one resume at a time)
        setFiles([file])
      });

      myDropzone.current.on("removedfile", (file: DropzoneFile) => {
        // Only delete from server if this is a user-initiated removal (clicking the X button)
        // Don't delete if it's being removed programmatically after an error or after successful upload
        if (file.status !== 'error' && file.status !== 'success') {
          submitProfileResumeDeleteField(state.profile, dispatch, state.authenticity_token, setFiles)
        }
        setFilenameWarning('');
        setUploadError('');
        setUploadSuccess(false);
      });

      myDropzone.current.on("success", (file: DropzoneFile, response: any) => {
        setIsUploading(false)
        setUploadError('')

        // Check if the server actually saved the resume
        if (response && response.resume_file_name) {
          setUploadSuccess(true)
          // Auto-hide success message after 5 seconds
          setTimeout(() => setUploadSuccess(false), 5000)

          // Update the context with the new resume
          dispatch({
            type: ACTIONS.SET_RESUME,
            payload: {
              resume: file,
              resume_file_name: response.resume_file_name
            }
          })

          // If resume parsing was started, begin polling for status
          if (response.resume_parse_id) {
            setResumeParseId(response.resume_parse_id)
            setIsParsing(true)
          }

          // Don't remove the file from dropzone - let it show as uploaded
          // The user can manually remove it if they want to upload a different one
        } else {
          console.error("Resume upload response missing resume_file_name:", response)
          setUploadError("Resume upload may have failed - please try again")
          // Only remove the file if upload actually failed
          setTimeout(() => {
            myDropzone.current?.removeFile(file)
          }, 100)
        }
      });

      myDropzone.current.on("error", (file: DropzoneFile, errorMessage: any, xhr: any) => {
        console.error("Upload error:", file, errorMessage)
        console.error("Server response:", xhr?.responseText)
        setIsUploading(false)

        // Parse error message
        let displayError = "Failed to upload resume. Please try again."

        // Try to parse JSON response if available
        if (xhr && xhr.responseText) {
          try {
            const response = JSON.parse(xhr.responseText)
            if (response.error) {
              displayError = response.error
              if (response.message) {
                displayError = response.message
              } else if (response.details && Array.isArray(response.details)) {
                displayError += ": " + response.details.join(", ")
              }
            }
          } catch (e) {
            // If not JSON, use the raw response
            console.error("Failed to parse error response:", e)
          }
        } else if (typeof errorMessage === 'string') {
          displayError = errorMessage
        } else if (errorMessage?.error) {
          displayError = errorMessage.error
          if (errorMessage.details && Array.isArray(errorMessage.details)) {
            displayError += ": " + errorMessage.details.join(", ")
          } else if (errorMessage.message) {
            displayError = errorMessage.message
          }
        } else if (errorMessage?.message) {
          displayError = errorMessage.message
        }

        setUploadError(displayError)

        // Remove the failed file from the dropzone
        myDropzone.current?.removeFile(file)
      });

      myDropzone.current.on("complete", (file: DropzoneFile) => {
        setIsUploading(false)
      });

      myDropzone.current.on("sending", (file: DropzoneFile) => {
        setIsUploading(true)
        setUploadError('')
        setUploadSuccess(false)
      });

      myDropzone.current.on("canceled", (file: DropzoneFile) => {
        setIsUploading(false)
      });
    }
  }, [state.profile.id]);


  React.useEffect(() => {
    if (!myDropzone.current) return;

    // Clear existing files first
    myDropzone.current.removeAllFiles(true);

    if (state.profile.resume_file_name == null || state.profile.resume_file_name === '') {
      setFiles([])
    } else {
      const mockFile: DropzoneFile = {
        id: "1",
        name: state.profile.resume_file_name || "Resume",
        size: state.profile.resume_file_size || "123",
        url: "",
        thumbnail: "/doc.png"
      }

      myDropzone.current.emit("addedfile", mockFile);
      myDropzone.current.emit("complete", mockFile);
      mockFile.status = 'success';
      myDropzone.current.emit("thumbnail", mockFile, "/doc.png");
      setFiles([mockFile])
    }
  }, [state.profile.resume_file_name])

  // Poll for resume parsing status
  React.useEffect(() => {
    if (!resumeParseId || !isParsing) return;

    const pollInterval = setInterval(async () => {
      try {
        const response = await fetch(`/api/v1/resume_parses/${resumeParseId}/status`, {
          method: 'GET',
          credentials: 'include',
          headers: {
            'Accept': 'application/json',
            'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''
          }
        });

        if (!response.ok) {
          throw new Error('Failed to check parsing status');
        }

        const data = await response.json();

        if (data.status === 'completed') {
          setIsParsing(false);
          // Reload the page to show the ResumeImport component with parsed data
          window.location.reload();
        } else if (data.status === 'failed') {
          setIsParsing(false);
          setUploadError('Resume parsing failed. Your resume was uploaded, but we couldn\'t extract the data automatically.');
        }
      } catch (error) {
        console.error('Error polling parse status:', error);
        // Don't stop polling on transient errors
      }
    }, 2000); // Poll every 2 seconds

    return () => clearInterval(pollInterval);
  }, [resumeParseId, isParsing])

  return (
    <>
      <div id="DocumentDropzone" className="DocumentDropzone container my-3">

        {/* Upload error message */}
        {uploadError && (
          <div className="alert alert-danger alert-dismissible fade show mt-2" role="alert">
            <i className="fas fa-exclamation-circle me-2"></i>
            <strong>Upload Error:</strong> {uploadError}
            <button
              type="button"
              className="btn-close"
              onClick={() => setUploadError('')}
              aria-label="Close"
            ></button>
          </div>
        )}

        {/* Upload success message */}
        {uploadSuccess && (
          <div className="alert alert-success alert-dismissible fade show mt-2" role="alert">
            <i className="fas fa-check-circle me-2"></i>
            <strong>Success!</strong> Your resume has been uploaded successfully.
            <button
              type="button"
              className="btn-close"
              onClick={() => setUploadSuccess(false)}
              aria-label="Close"
            ></button>
          </div>
        )}

        {/* Filename validation warning */}
        {filenameWarning && (
          <div className="alert alert-warning alert-dismissible fade show mt-2" role="alert">
            <i className="fas fa-exclamation-triangle me-2"></i>
            <div style={{ whiteSpace: 'pre-line' }}>
              <strong>Filename will be cleaned up:</strong><br />
              {filenameWarning}
            </div>
            <button
              type="button"
              className="btn-close"
              onClick={() => setFilenameWarning('')}
              aria-label="Close"
            ></button>
          </div>
        )}

        {/* Upload progress indicator */}
        {isUploading && (
          <div className="alert alert-info mt-2" role="alert">
            <div className="d-flex align-items-center">
              <div className="spinner-border spinner-border-sm me-2" role="status">
                <span className="visually-hidden">Uploading...</span>
              </div>
              <span>Uploading your resume...</span>
            </div>
          </div>
        )}

        {/* Resume parsing indicator */}
        {isParsing && (
          <div className="alert alert-info mt-2" role="alert" style={{ backgroundColor: '#cfe2ff', borderColor: '#b6d4fe' }}>
            <div className="d-flex align-items-center">
              <div className="spinner-border spinner-border-sm me-2 text-primary" role="status">
                <span className="visually-hidden">Processing...</span>
              </div>
              <div>
                <strong className="text-dark">Processing your resume...</strong>
                <div className="small" style={{ color: '#495057' }}>
                  We're extracting your experience and skills. The page will refresh automatically when complete.
                </div>
              </div>
            </div>
          </div>
        )}

        <div
          id="DocumentDropzoneContainer"
          ref={dropzoneRef}
          className="dropzone bg-light"
          style={{
            minHeight: '140px',
            border: '2px dashed #dee2e6',
            borderRadius: '0.375rem',
            padding: '2rem',
            marginTop: '1rem',
            transition: 'all 0.3s ease',
            cursor: 'pointer',
            backgroundColor: '#f8f9fa'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.borderColor = '#0d6efd';
            e.currentTarget.style.backgroundColor = '#e7f1ff';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.borderColor = '#dee2e6';
            e.currentTarget.style.backgroundColor = '#f8f9fa';
          }}
        >
          <div className="dz-message text-center" style={{ margin: '0' }}>
            <i className="fas fa-cloud-upload-alt text-primary mb-3" style={{ fontSize: '3rem', opacity: '0.7' }}></i>
            <h5 className="text-secondary mb-2">Drop files here to upload</h5>
            <p className="text-muted small mb-0">or click to browse your computer</p>
            <p className="text-muted small mt-2">
              <i className="fas fa-info-circle"></i> Accepted formats: PDF, DOC, DOCX, JPG, PNG
            </p>
          </div>
        </div>
      </div>
    </>
  )
}
